/**
 * Print-failure visibility helpers (R45).
 *
 * The Tauri shell's print-method dispatch emits a `print-chain-failed` event
 * when EVERY configured method for an invoice type failed or fell through
 * (previously only the per-attempt `print-result` event existed, and its only
 * listener lived on the Printing Options page — so a real print from any
 * other screen could end silently, which is exactly what happened on a
 * machine configured `pooja.method.order=native` with a 4inch template).
 *
 * Pure functions here so both the global monitor toast and the Printing
 * Options inline warning are spec-covered without any Tauri plumbing.
 */

/** One attempted method of a failed dispatch (from the Rust shell, camelCase). */
export interface PrintAttempt {
    method: string;
    code: number;
    reason: string;
}

/** Payload of the `print-chain-failed` Tauri event. */
export interface PrintChainFailedPayload {
    printType: string;
    template: string;
    printer: string;
    attempts: PrintAttempt[];
}

/** All dispatch methods the shell knows (mirrors the settings page list). */
export const ALL_PRINT_METHODS = ['java', 'pdf', 'native'] as const;

/**
 * Operator-facing message for a fully failed print chain: which methods were
 * tried and why each ended, plus which methods are not enabled at all.
 */
export function buildChainFailureMessage(payload: PrintChainFailedPayload): string {
    const tried = payload.attempts.length
        ? payload.attempts.map(a => `${a.method} — ${a.reason}`).join('; ')
        : 'no print methods are enabled';
    const attempted = new Set(payload.attempts.map(a => a.method));
    const notEnabled = ALL_PRINT_METHODS.filter(m => !attempted.has(m));
    let msg = `Print failed for '${payload.printType}' (template ${payload.template}`
        + (payload.printer ? `, printer '${payload.printer}'` : '')
        + `): ${tried}.`;
    if (notEnabled.length) {
        msg += ` Not enabled: ${notEnabled.join(', ')}.`;
    }
    if (!attempted.has('java')) {
        msg += ` Enable the 'java' method in Settings → Printing Options.`;
    }
    return msg;
}

/** The subset of the settings page's PrintProfile the warning rule needs. */
export interface PrintProfileLike {
    type: string;
    template: string;
    methods: Array<{ id: string; enabled: boolean }>;
}

/**
 * Templates the Rust native renderer implements per print type (R50 — keep in
 * lockstep with src-tauri/src/native_layouts.rs map_legacy_to_pages). A type
 * missing from this map has NO native renderer; an empty-string template
 * entry '*' means every template value is accepted (membership's single
 * layout, like the jar).
 */
export const NATIVE_TEMPLATE_SUPPORT: Record<string, string[]> = {
    pooja: ['3inch', '4inch', '6inch', 'a4', 'custom'],
    donation: ['3inch', '4inch', '6inch', 'a4', 'custom'],
    expense: ['3inch', '6inch', 'a4', 'custom'],
    auditorium: ['3inch', '6inch', 'a4'],
    salary: ['3inch', '6inch', 'a4'],
    membership: ['*'],
    marriage: ['a4'],
};

/** True when the native renderer can serve this (type, template) combo. */
export function nativeSupports(type: string, template: string): boolean {
    const templates = NATIVE_TEMPLATE_SUPPORT[(type || '').toLowerCase()];
    if (!templates) {
        return false;
    }
    return templates.includes('*') || templates.includes((template || '').toLowerCase());
}

/**
 * Inline warning for a Printing Options profile card — configurations that
 * are guaranteed (or very likely) to end every print chain with no output.
 * Returns null when the configuration can print. Warn only — never mutates
 * or reorders the operator's configuration.
 */
export function printProfileWarning(profile: PrintProfileLike): string | null {
    const enabled = profile.methods.filter(m => m.enabled).map(m => m.id);
    if (!enabled.length) {
        return 'No print methods are enabled — Print will do nothing for this type. '
            + 'Enable at least the \'java\' method.';
    }
    if (enabled.every(id => id === 'native')
        && !nativeSupports(profile.type, profile.template)) {
        return 'Only the \'native\' method is enabled, but native does not print '
            + `'${profile.type}' on the '${profile.template}' template — every print will fail. `
            + 'Enable \'java\' as well, or pick a template native supports.';
    }
    return null;
}

/**
 * Summary line for the print.log dispatch-request entry sent from the
 * Angular layer (the Rust shell logs the config/methods/outcomes itself;
 * this line records what the UI asked for, with the invoice numbers it knows).
 */
export function describePrintDispatch(data: any, duplicate: boolean): { printType: string; message: string } {
    const items: any[] = Array.isArray(data) ? data : (data != null ? [data] : []);
    const printType = String(items[0]?.printType || 'pooja').toLowerCase();
    const numbers = items
        .map(item => item?.invoiceNumber)
        .filter((n: any) => n != null && String(n).trim() !== '')
        .map((n: any) => String(n));
    const shown = numbers.length > 8
        ? numbers.slice(0, 8).concat(`+${numbers.length - 8} more`)
        : numbers;
    return {
        printType,
        message: `ui dispatch requested: ${items.length} invoice(s) [${shown.join(',')}] duplicate=${duplicate}`
    };
}
